import { useCallback } from 'react';
import type { AttendanceStatus, Meeting } from '../../../graphql/types';
import { useUpdateMeetingAttendanceMutation } from '../gql/_gen_/meeting.gql';

interface UseAttendanceProps {
  meetingId: string;
  onMeetingUpdate?: (meeting: Meeting) => void;
}

export const useAttendance = ({ meetingId, onMeetingUpdate }: UseAttendanceProps) => {
  const [updateAttendanceMutation, { loading, error }] = useUpdateMeetingAttendanceMutation({
    // Error handling
    onError: (error) => {
      console.error('Failed to update meeting attendance:', error);
    },
  });

  const updateAttendanceStatus = useCallback(
    async (attendanceStatus: AttendanceStatus, currentMeeting?: Meeting) => {
      try {
        // Optimistic update: immediately update the UI with expected result
        if (currentMeeting && onMeetingUpdate) {
          onMeetingUpdate({
            ...currentMeeting,
            attendanceStatus,
            updatedAt: new Date().toISOString(),
          });
        }

        const result = await updateAttendanceMutation({
          variables: {
            id: meetingId,
            attendanceStatus,
          },
        });

        // Update with real server data when mutation completes
        if (result.data?.updateMeetingAttendance && onMeetingUpdate) {
          onMeetingUpdate(result.data.updateMeetingAttendance);
        }

        return result.data?.updateMeetingAttendance;
      } catch (err) {
        console.error('Error updating attendance status:', err);
        // On error, revert to original meeting data if available
        if (currentMeeting && onMeetingUpdate) {
          onMeetingUpdate(currentMeeting);
        }
        throw err;
      }
    },
    [meetingId, updateAttendanceMutation, onMeetingUpdate],
  );

  return {
    updateAttendanceStatus,
    loading,
    error,
  };
};
